import { HttpErrorResponse } from '@angular/common/http';
import type { AccountLoginUser } from '@automatick/shared-client-ng';
import { AuthService, type Session, type Token } from '@seech/auth-ng';
import { RealTimeCommunicationService } from '@seech/core-ng';
import { NotificationHubContract } from '@automatick/shared-ts';
import {
  ChannelClientService,
  ChatSessionRealtimeBootstrapResponse,
  ConversationClientService,
  ProjectsClientService,
} from '@automatick/shared-ng';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppStateService } from './app-state.service';
import { CurrentDeviceContextService } from './current-device-context.service';
import { buildIdleRealtimeConnectionHealth } from '../shared/realtime/realtime-connection-health';
import {
  PendingOutboundMessage,
  WebChatRequestErrorBody,
  WebChatState,
} from './web-chat.models';

export class WebChatStateStore {
  readonly destroy$ = new Subject<void>();
  startRealtimeConnectionPromise: Promise<void> | null = null;
  realtimeBootstrap: ChatSessionRealtimeBootstrapResponse | null = null;
  activeLiveSessionId: string | null = null;
  activeLiveThreadId: string | null = null;
  latestRenderEventSequence: number | null = null;
  readonly joinedWorkRequestIds = new Set<string>();

  awaitingResponseTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  lastOutboundTimestamp: number | null = null;
  activeRequestCancel$: Subject<void> | null = null;
  pendingOutbound: PendingOutboundMessage | null = null;

  readonly stateSubject = new BehaviorSubject<WebChatState>({
    phase: 'idle',
    binding: null,
    session: null,
    sessions: [],
    projects: [],
    selectedProjectId: null,
    uxCapabilities: null,
    messages: [],
    sessionFiles: [],
    isSending: false,
    isStoppingResponse: false,
    isAwaitingResponse: false,
    activeThoughtText: null,
    isLoadingHistory: false,
    isLoadingFiles: false,
    isSearchingSessions: false,
    isSearchingMessages: false,
    sessionSearchTerm: '',
    messageSearchTerm: '',
    liveTransport: 'idle',
    liveStatusMessage: '',
    realtimeHealth: buildIdleRealtimeConnectionHealth(
      NotificationHubContract.ReceiveMethod,
    ),
    latestRenderEvent: null,
    latestSearchRenderEvent: null,
    latestHealthCheckRenderEvent: null,
    recentRenderEvents: [],
    presentationRenderEvents: [],
    thoughtChain: [],
    thoughtTraces: [],
    thoughtTraceEntriesById: {},
    loadingThoughtTraceIds: [],
    activeThoughtTraceId: null,
    finalRenderMessage: null,
    isThoughtChainActive: false,
    errorMessage: '',
  });

  readonly state$ = this.stateSubject.asObservable();

  get stateSnapshot(): WebChatState {
    return this.stateSubject.value;
  }

  constructor(
    readonly channelClient: ChannelClientService,
    readonly chatClient: ConversationClientService,
    readonly projectsClient: ProjectsClientService,
    readonly authService: AuthService<AccountLoginUser, Session, Token>,
    readonly appState: AppStateService,
    readonly currentDeviceContext: CurrentDeviceContextService,
    readonly realtime: RealTimeCommunicationService,
  ) {}

  patch(partial: Partial<WebChatState>): void {
    const nextState = { ...this.stateSubject.value, ...partial };
    this.stateSubject.next(nextState);
  }

  resolveRequestError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const responseBody = error.error as
        WebChatRequestErrorBody | string | null;
      if (typeof responseBody === 'string' && responseBody.trim().length > 0) {
        return responseBody.trim();
      }

      if (responseBody && typeof responseBody === 'object') {
        const userMessage = responseBody.userMessage;
        if (typeof userMessage === 'string' && userMessage.trim().length > 0) {
          return userMessage.trim();
        }

        const message = responseBody.message;
        if (typeof message === 'string' && message.trim().length > 0) {
          return message.trim();
        }
      }

      if (
        typeof error.message === 'string' &&
        error.message.trim().length > 0
      ) {
        return error.message.trim();
      }
    }

    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message.trim();
    }

    return 'Failed to send message.';
  }

  beginAwaitingResponse(): void {
    this.lastOutboundTimestamp = Date.now();
    this.clearAwaitingResponseTimeout();
    this.patch({
      isAwaitingResponse: true,
      isStoppingResponse: false,
      activeThoughtText: null,
      recentRenderEvents: [],
      latestRenderEvent: null,
      latestSearchRenderEvent: null,
      latestHealthCheckRenderEvent: null,
      thoughtChain: [],
      activeThoughtTraceId: null,
      finalRenderMessage: null,
      isThoughtChainActive: true,
    });

    this.awaitingResponseTimeoutHandle = setTimeout(() => {
      this.awaitingResponseTimeoutHandle = null;
      if (this.stateSubject.value.isAwaitingResponse) {
        this.patch({
          isAwaitingResponse: false,
          activeThoughtText: null,
          finalRenderMessage: null,
          isThoughtChainActive: false,
        });
      }
    }, 120_000);
  }

  clearAwaitingResponseTimeout(): void {
    if (this.awaitingResponseTimeoutHandle) {
      clearTimeout(this.awaitingResponseTimeoutHandle);
      this.awaitingResponseTimeoutHandle = null;
    }
  }

  createActiveRequestCancel$(): Subject<void> {
    this.cancelActiveRequestTransport();
    const cancel$ = new Subject<void>();
    this.activeRequestCancel$ = cancel$;
    return cancel$;
  }

  completeActiveRequestCancel$(cancel$: Subject<void>): void {
    if (this.activeRequestCancel$ === cancel$) {
      this.activeRequestCancel$ = null;
    }

    if (!cancel$.closed) {
      cancel$.complete();
    }
  }

  cancelActiveRequestTransport(): void {
    const cancel$ = this.activeRequestCancel$;
    this.activeRequestCancel$ = null;
    if (!cancel$ || cancel$.closed) {
      return;
    }

    cancel$.next();
    cancel$.complete();
  }

  resetAwaitingResponseState(): void {
    this.lastOutboundTimestamp = null;
    this.clearAwaitingResponseTimeout();
    this.patch({
      isSending: false,
      isStoppingResponse: false,
      isAwaitingResponse: false,
      activeThoughtText: null,
      finalRenderMessage: null,
      isThoughtChainActive: false,
      thoughtChain: [],
      activeThoughtTraceId: null,
    });
  }
}
